import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { track } from "@/lib/api";
import { ArrowLeft, CheckCircle, Circle, BookOpen } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LearnPathDetailPage() {
  const { slug } = useParams();
  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeLesson, setActiveLesson] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/learn/paths/${slug}`);
      setPath(res.data);
      if (!activeLesson && res.data?.lessons?.length) {
        const next = res.data.lessons.find((l) => !l.completed) || res.data.lessons[0];
        setActiveLesson(next);
      }
    } catch {
      toast.error("Couldn't load this path");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const completeLesson = async (lesson) => {
    try {
      await api.post(`/learn/lessons/${lesson.id}/complete`);
      track("lesson_completed", { lesson_id: lesson.id, path_slug: slug, title: lesson.title });
      toast.success("Lesson completed");
      const res = await api.get(`/learn/paths/${slug}`);
      setPath(res.data);
      const idx = res.data.lessons.findIndex((l) => l.id === lesson.id);
      const next = res.data.lessons.slice(idx + 1).find((l) => !l.completed);
      setActiveLesson(next || res.data.lessons[idx]);
    } catch {
      toast.error("Could not save progress");
    }
  };

  if (loading) {
    return (
      <div className="px-5 py-10 max-w-3xl mx-auto text-center text-[#8B7355] font-body text-sm">
        Loading path…
      </div>
    );
  }
  if (!path) return null;

  const pct = path.total_lessons ? Math.round((path.completed_lessons / path.total_lessons) * 100) : 0;

  return (
    <div className="px-5 md:px-8 py-4 max-w-3xl mx-auto" data-testid="learn-path-detail">
      <Link to="/learn" className="inline-flex items-center gap-1 text-[#8B7355] text-sm font-body mb-4">
        <ArrowLeft size={16} /> Back to Learn
      </Link>

      <div className={`glass-card-strong p-6 mb-6 ${path.color}`}>
        <div className="flex items-center gap-2 text-[#C4A265] text-[10px] uppercase tracking-[0.2em] font-body font-medium mb-2">
          <BookOpen size={14} weight="fill" />
          {path.is_foundation ? "Foundation" : "Path"}
        </div>
        <h1 className="font-heading text-3xl text-[#2D1B0E] font-medium mb-1">{path.name}</h1>
        <p className="font-body text-sm text-[#5C4033] mb-3">{path.description}</p>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-1.5 bg-[#EDE6DC] rounded-full">
            <div className="h-1.5 bg-[#C4A265] rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[10px] font-body text-[#8B7355] whitespace-nowrap">
            {path.completed_lessons}/{path.total_lessons} · {pct}%
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        {/* Lessons list */}
        <div className="space-y-2">
          <h2 className="font-heading text-lg text-[#2D1B0E] font-medium mb-2">Lessons</h2>
          {path.lessons.map((lesson, i) => (
            <button
              key={lesson.id}
              data-testid={`lesson-item-${lesson.id}`}
              onClick={() => setActiveLesson(lesson)}
              className={`w-full text-left glass-card p-3 flex items-center gap-3 transition-all hover:-translate-y-0.5 ${
                activeLesson?.id === lesson.id ? "ring-1 ring-[#C4A265]" : ""
              }`}
            >
              {lesson.completed ? (
                <CheckCircle size={20} weight="fill" className="text-[#C4A265] flex-shrink-0" />
              ) : (
                <Circle size={20} className="text-[#B8A088] flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-body text-sm font-medium text-[#2D1B0E] truncate">
                  {i + 1}. {lesson.title}
                </p>
                <p className="font-body text-[10px] text-[#8B7355]">{lesson.duration_min} min</p>
              </div>
            </button>
          ))}
        </div>

        {/* Active lesson */}
        {activeLesson && (
          <div className="glass-card-strong p-6" data-testid="active-lesson">
            <h3 className="font-heading text-2xl text-[#2D1B0E] font-medium mb-2">{activeLesson.title}</h3>
            {activeLesson.scripture && (
              <p className="text-[#C4A265] font-body text-xs tracking-wider uppercase mb-3">
                {activeLesson.scripture}
              </p>
            )}
            <p className="font-body text-sm text-[#5C4033] leading-relaxed mb-6 whitespace-pre-line">
              {activeLesson.content}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => completeLesson(activeLesson)}
                disabled={activeLesson.completed}
                data-testid="complete-lesson-btn"
                className="px-6 py-2.5 rounded-full glass-card text-[#5C4033] font-body text-sm font-medium hover:bg-white/60 transition-all disabled:opacity-60"
              >
                {activeLesson.completed ? "Completed" : "Mark Complete"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
